import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, Car, MapPin, Filter, Trash2, Plus, History } from 'lucide-react';
import '../styles/tiquesHistoryStyle.css';
import vTicketService from '../services/vTicketsService';
import parkingService from '../services/parkingService';
import authService from '../services/loginService';

const TiquetesHisto = () => {
  // Estados para el formulario de registro manual
  const [selectedDate, setSelectedDate] = useState(new Date()); // Fecha actual por defecto
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedParking, setSelectedParking] = useState('');
  const [entryTime, setEntryTime] = useState(() => {
    // Hora actual por defecto
    const now = new Date();
    return now.toTimeString().slice(0, 5); // HH:MM format
  });
  const [parkingTariff, setParkingTariff] = useState(0); // Tarifa del parqueo seleccionado

  // Estados para el historial
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para datos del backend
  const [parkings, setParkings] = useState([]);
  const [userCars] = useState(['MLS 135', 'ABC 123', 'XYZ 789']); // Mock - obtener del perfil del usuario

  const currentUser = authService.getCurrentUser();

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Cargar tarifa del parqueo seleccionado
  const loadParkingTariff = async (parkingId) => {
    if (!parkingId) {
      setParkingTariff(0);
      return;
    }

    try {
      // Intentar obtener los datos completos del parqueo
      const parking = parkings.find(p => p._id === parkingId);
      if (parking && parking.tarify) {
        setParkingTariff(parking.tarify);
        return;
      }

      // Si no está en la lista cargada, hacer petición individual
      const response = await parkingService.getParkingById(parkingId);
      if (response && response.tarify) {
        setParkingTariff(response.tarify);
      } else {
        setParkingTariff(0);
      }
    } catch (error) {
      console.error('Error al cargar tarifa del parqueo:', error);
      setParkingTariff(0);
    }
  };

  // Efecto para cargar la tarifa cuando cambia el parqueo seleccionado
  useEffect(() => {
    if (selectedParking) {
      loadParkingTariff(selectedParking);
    } else {
      setParkingTariff(0);
    }
  }, [selectedParking]);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadParkings(),
        loadUserTickets()
      ]);
    } catch (error) {
      setError('Error al cargar datos iniciales');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadParkings = async () => {
    try {
      const response = await parkingService.getAllParkings();
      setParkings(response.parkings || response || []);
    } catch (error) {
      console.error('Error al cargar parqueos:', error);
    }
  };

  const loadUserTickets = async () => {
    if (!currentUser?.idCard) return;

    try {
      setLoading(true);

      // Usar el método mejorado que maneja errores automáticamente
      const tickets = await vTicketService.getVTicketsByIdCard(currentUser.idCard);

      const formattedTickets = tickets.map(ticket => {
        // Usar la función de formateo del servicio
        const formatted = vTicketService.formatTicketForDisplay(ticket);
        return {
          ...formatted,
          // Ajustar el estado para la UI
          status: ticket.status === 'Realizado' ? 'Realizado' :
            ticket.status === 'En Proceso' ? 'Pendiente' :
              ticket.status
        };
      });

      setTickets(formattedTickets);
      setFilteredTickets(formattedTickets);
    } catch (error) {
      console.error('Error al cargar tickets:', error);
      setError('Error al cargar el historial de tickets: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Funciones para el calendario
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Lunes = 0

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const changeMonth = (increment) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + increment, selectedDate.getDate()));
  };

  const selectDay = (day) => {
    if (day) {
      setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day));
    }
  };

  // Manejar registro manual de ticket
  const handleManualTicketSubmit = async (e) => {
    e.preventDefault();

    if (!selectedParking || !selectedCar || !entryTime) {
      setError('Por favor complete todos los campos obligatorios');
      return;
    }

    if (!currentUser?.idCard) {
      setError('Usuario no autenticado');
      return;
    }

    if (parkingTariff <= 0) {
      setError('No se pudo obtener la tarifa del parqueo seleccionado');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const entryDateTime = new Date(`${selectedDate.toDateString()} ${entryTime}`);

      const ticketData = {
        idCard: currentUser.idCard,
        idParking: selectedParking,
        vehiclesPlate: selectedCar,
        billDateIn: entryDateTime.toISOString(),
        billDateOut: null, // No hay hora de salida
        totalpaid: 0, // Se calculará cuando termine el ticket
        status: 'En Proceso'
      };

      // Validar datos antes de enviar
      const validation = vTicketService.validateTicketData(ticketData);
      if (!validation.isValid) {
        setError('Errores de validación: ' + validation.errors.join(', '));
        return;
      }

      const result = await vTicketService.createVTicket(ticketData);

      if (result.success) {
        setSuccess('Ticket registrado exitosamente');

        // Limpiar formulario (mantener fecha y hora actual)
        setSelectedCar('');
        setSelectedParking('');
        setParkingTariff(0);

        // Recargar tickets
        await loadUserTickets();
      } else {
        setError('Error al registrar el ticket: ' + (result.message || 'Error desconocido'));
      }

    } catch (error) {
      console.error('Error al registrar ticket:', error);
      setError('Error al registrar el ticket: ' + (error.response?.data?.message || error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Filtrar tickets por fecha
  const handleFilterTickets = async () => {
    if (!filterDate) {
      setFilteredTickets(tickets);
      return;
    }

    try {
      setLoading(true);

      // Usar el método de filtrado por rango de fechas del servicio
      const filterDateStart = new Date(filterDate);
      const filterDateEnd = new Date(filterDate);
      filterDateEnd.setHours(23, 59, 59, 999); // Incluir todo el día

      const filteredTickets = await vTicketService.getTicketsByDateRange(
        currentUser.idCard,
        filterDateStart.toISOString(),
        filterDateEnd.toISOString()
      );

      const formattedFilteredTickets = filteredTickets.map(ticket => {
        const formatted = vTicketService.formatTicketForDisplay(ticket);
        return {
          ...formatted,
          status: ticket.status === 'Realizado' ? 'Realizado' :
            ticket.status === 'En Proceso' ? 'Pendiente' :
              ticket.status
        };
      });

      setFilteredTickets(formattedFilteredTickets);
    } catch (error) {
      console.error('Error al filtrar tickets:', error);
      setError('Error al filtrar tickets: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de tickets
  const toggleTicketSelection = (ticketId) => {
    setSelectedTickets(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  // Eliminar tickets seleccionados
  const handleDeleteSelectedTickets = async () => {
    if (selectedTickets.length === 0) {
      setError('No hay tickets seleccionados');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${selectedTickets.length} ticket(s)?`)) {
      return;
    }

    try {
      setLoading(true);

      // CORREGIDO: Usar Promise.allSettled directamente sin el método deleteMultipleVTickets
      const deletePromises = selectedTickets.map(async (ticketId) => {
        try {
          const response = await vTicketService.deleteVTicket(ticketId);
          return { success: true, ticketId, response };
        } catch (error) {
          console.error(`Error eliminando ticket ${ticketId}:`, error);
          return { success: false, ticketId, error };
        }
      });

      const results = await Promise.allSettled(deletePromises);

      const successful = results.filter(result =>
        result.status === 'fulfilled' && result.value.success
      ).length;

      const failed = results.filter(result =>
        result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
      ).length;

      if (failed > 0) {
        setError(`Se eliminaron ${successful} tickets, pero ${failed} fallaron`);
      } else {
        setSuccess(`${successful} ticket(s) eliminado(s) exitosamente`);
      }

      setSelectedTickets([]);
      await loadUserTickets();

    } catch (error) {
      console.error('Error al eliminar tickets:', error);
      setError('Error al eliminar tickets: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Limpiar mensajes después de unos segundos
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const ManualTicketEntry = () => (
    <div className="ths-manual-entry-container">
      <div className="ths-section-header">
        <Plus className="ths-header-icon" size={20} />
        <h2 className="ths-section-title">Registrar Tiquete Manual</h2>
      </div>

      <form onSubmit={handleManualTicketSubmit} className="ths-form">
        {/* Selector de Auto */}
        <div className="ths-form-group">
          <label className="ths-form-label">
            <Car size={16} />
            Auto
          </label>
          <select
            value={selectedCar}
            onChange={(e) => setSelectedCar(e.target.value)}
            className="ths-select"
            required
          >
            <option value="">Seleccione un auto</option>
            {userCars.map(car => (
              <option key={car} value={car}>{car}</option>
            ))}
          </select>
        </div>

        {/* Selector de Parqueo */}
        <div className="ths-form-group">
          <label className="ths-form-label">
            <MapPin size={16} />
            Parqueo
          </label>
          <select
            value={selectedParking}
            onChange={(e) => setSelectedParking(e.target.value)}
            className="ths-select"
            required
          >
            <option value="">Seleccione un parqueo</option>
            {parkings.map(parking => (
              <option key={parking._id} value={parking._id}>
                {parking.parkingName}
              </option>
            ))}
          </select>
        </div>

        {/* Calendario */}
        <div className="ths-form-group">
          <label className="ths-form-label">
            <Calendar size={16} />
            Día de ingreso
          </label>
          <div className="ths-calendar-wrapper">
            <div className="ths-calendar-header">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="ths-calendar-nav-button"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="ths-calendar-month-year">
                {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
              </span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="ths-calendar-nav-button"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="ths-calendar-grid">
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                <div key={day} className="ths-calendar-day-header">{day}</div>
              ))}
              {getDaysInMonth(selectedDate).map((day, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => selectDay(day)}
                  disabled={!day}
                  className={`ths-calendar-day-button ${day === selectedDate.getDate() ? 'selected' : ''
                    }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Horario de ingreso */}
        <div className="ths-form-group">
          <label className="ths-form-label">
            <Clock size={16} />
            Hora de ingreso
          </label>
          <input
            type="time"
            value={entryTime}
            onChange={(e) => setEntryTime(e.target.value)}
            className="ths-time-input"
            required
          />
        </div>

        {/* Información de tarifa */}
        <div className="ths-form-group">
          <label className="ths-form-label">Tarifa por hora</label>
          <div className="ths-tariff-display">
            {selectedParking ? (
              parkingTariff > 0 ? (
                <span className="ths-tariff-amount">¢{parkingTariff.toLocaleString()}</span>
              ) : (
                <span className="ths-tariff-loading">Cargando tarifa...</span>
              )
            ) : (
              <span className="ths-tariff-placeholder">Seleccione un parqueo</span>
            )}
          </div>
        </div>

        {/* Botón de Acción */}
        <button
          type="submit"
          className="ths-action-button"
          disabled={loading}
        >
          {loading ? 'Registrando...' : 'Registrar Tiquete Manual'}
        </button>
      </form>
    </div>
  );

  const TicketHistory = () => (
    <div className="ths-ticket-history-container">
      <div className="ths-section-header">
        <History className="ths-header-icon" size={20} />
        <h2 className="ths-section-title">Historial de Tiquetes</h2>
      </div>

      <div className="ths-filter-controls">
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="ths-date-filter"
        />
        <button
          onClick={handleFilterTickets}
          className="ths-filter-button"
        >
          <Filter size={16} />
          Filtrar
        </button>
      </div>

      {loading && <div className="ths-loading">Cargando...</div>}

      {!loading && filteredTickets.length === 0 && (
        <div className="ths-no-data">No hay tiquetes para mostrar</div>
      )}

      {!loading && filteredTickets.length > 0 && (
        <>
          <div className="ths-table-header">
            <div>Día</div>
            <div>Hora</div>
            <div>Placa</div>
            <div>Estado</div>
            <div>Monto</div>
          </div>

          {filteredTickets.map((ticket) => (
            <div key={ticket.id} className="ths-table-row">
              <div className="ths-table-cell-with-checkbox">
                <input
                  type="checkbox"
                  className="ths-table-checkbox"
                  checked={selectedTickets.includes(ticket.id)}
                  onChange={() => toggleTicketSelection(ticket.id)}
                />
                {ticket.date}
              </div>
              <div className="ths-table-cell">{ticket.time}</div>
              <div className="ths-table-cell">{ticket.plate}</div>
              <div className={`ths-table-cell ths-status-${ticket.status.toLowerCase().replace(' ', '-')}`}>
                {ticket.status}
              </div>
              <div className="ths-table-cell">¢{ticket.amount.toLocaleString()}</div>
            </div>
          ))}

          <div className="ths-delete-button-container">
            <button
              onClick={handleDeleteSelectedTickets}
              className="ths-delete-button"
              disabled={selectedTickets.length === 0 || loading}
            >
              <Trash2 size={16} />
              Borrar Seleccionados ({selectedTickets.length})
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="ths-main-container">
      {/* Mensajes de error/éxito */}
      {error && (
        <div className="ths-alert ths-alert-error">
          {error}
        </div>
      )}
      {success && (
        <div className="ths-alert ths-alert-success">
          {success}
        </div>
      )}

      <div className="ths-main-content">
        <ManualTicketEntry />
        <TicketHistory />
      </div>
    </div>
  );
};

export default TiquetesHisto;